import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Theme } from '@jotpsych/ui/mui';
import { Tooltip, type TooltipProps } from '@mui/material';

import { WORK_SURFACE_SHADOW } from './workSurface';

/**
 * A rich hover detail for a table cell (GDN-3379).
 *
 * Hover opens after a beat, so a cursor crossing a list does not leave a
 * trail of popovers; keyboard focus opens at once; leaving closes after a
 * grace long enough for the pointer to travel into the surface, which is
 * itself hoverable; Escape closes (WCAG 1.4.13: dismissable, hoverable,
 * persistent). The surface is portalled, so the row's overflow clipping
 * cannot cut it off.
 *
 * A CLICK IS NOT THIS COMPONENT'S. Every cell that carries one sits in a
 * row whose whole area opens the claim, so the trigger listens to hover and
 * focus only: the click reaches the row untouched and neither opens nor
 * closes the detail. A pointer press does focus the trigger, as it focuses
 * anything with a tab index, and that focus is deliberately not an open:
 * the detail would otherwise flash under a click on Home, where the row's
 * click reads the queue before it navigates.
 *
 * `HoverWhy` in the claim workbench is this same behaviour with a string
 * payload and could adopt this primitive.
 */

/** Hover waits this long before opening: a cursor crossing a list opens nothing. */
export const HOVER_DETAIL_OPEN_DELAY_MS = 350;
/** Leaving the trigger or the surface closes after this grace: long enough to travel between them. */
export const HOVER_DETAIL_LEAVE_GRACE_MS = 140;
/** The surface's width cap: a glance beside the row, not a page over it. */
export const HOVER_DETAIL_MAX_WIDTH = 360;

export interface HoverDetailTriggerProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: () => void;
  onFocus: () => void;
  onBlur: () => void;
  /** A tab stop, so the detail is reachable without a pointer. */
  tabIndex: 0;
}

export interface HoverDetailProps {
  /**
   * What the surface shows. Rendered only while the detail is open, so a
   * container placed here starts its reads on open and never on row hover.
   */
  content: ReactNode;
  placement?: TooltipProps['placement'];
  /** Fires once each time the detail opens, by hover or by focus alike. */
  onOpen?: () => void;
  maxWidth?: number;
  /** The trigger: one element that takes a ref and spreads these props. */
  children: (trigger: HoverDetailTriggerProps) => ReactElement;
}

/**
 * The keyboard's focus ring on a trigger that is otherwise plain text. The
 * ring is drawn inside the box: the cell around it clips its overflow, and
 * a ring outside the box would be cut on every side.
 */
export const HOVER_DETAIL_TRIGGER_SX = {
  borderRadius: 0.5,
  outline: 'none',
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
} as const;

/* The theme's dense-surface data scale, the one it keeps for worklists, so
   the popover reads at the size of the rows it belongs to. */
const surfaceSx = (theme: Theme) => ({
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: WORK_SURFACE_SHADOW,
  color: 'text.primary',
  fontSize: theme.typography.dataMd.fontSize,
  fontWeight: 400,
  lineHeight: theme.typography.dataMd.lineHeight,
  maxWidth: HOVER_DETAIL_MAX_WIDTH,
  p: 1.5,
});

export function HoverDetail({
  content,
  placement = 'top-start',
  onOpen,
  maxWidth = HOVER_DETAIL_MAX_WIDTH,
  children,
}: HoverDetailProps) {
  const [open, setOpen] = useState(false);
  const [surface, setSurface] = useState<HTMLDivElement | null>(null);
  const popper = useRef<{ update: () => unknown } | null>(null);

  // Lazy query results can change the surface height after Popper chose a side.
  // Recalculate for both the loading-to-ready transition and cached reopens.
  useEffect(() => {
    if (!surface || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      void popper.current?.update();
    });
    observer.observe(surface);
    return () => observer.disconnect();
  }, [surface]);
  const timers = useRef<{
    enter?: ReturnType<typeof setTimeout>;
    leave?: ReturnType<typeof setTimeout>;
  }>({});
  /* Set by a pointer press and cleared by its release, so the focus the
     press brings can be told apart from a keyboard's. The release may land
     anywhere (a press dragged off the trigger releases elsewhere), so it is
     heard on the window; the pointer leaving and focus leaving clear it too. */
  const pressing = useRef(false);
  const onOpenRef = useRef(onOpen);

  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  /* Once per open, whichever gesture opened it. */
  useEffect(() => {
    if (open) onOpenRef.current?.();
  }, [open]);

  const releasePress = useCallback(() => {
    pressing.current = false;
  }, []);

  useEffect(() => () => window.removeEventListener('mouseup', releasePress), [releasePress]);

  const clearTimers = useCallback(() => {
    clearTimeout(timers.current.enter);
    clearTimeout(timers.current.leave);
  }, []);

  const show = useCallback(
    (delay: number) => {
      clearTimers();
      if (delay > 0) timers.current.enter = setTimeout(() => setOpen(true), delay);
      else setOpen(true);
    },
    [clearTimers],
  );

  const hide = useCallback(
    (delay: number) => {
      clearTimers();
      if (delay > 0) timers.current.leave = setTimeout(() => setOpen(false), delay);
      else setOpen(false);
    },
    [clearTimers],
  );

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <Tooltip
      describeChild
      disableFocusListener
      disableHoverListener
      disableTouchListener
      open={open}
      placement={placement}
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
          popperRef: instance => {
            popper.current = instance;
          },
        },
        tooltip: {
          ref: setSurface,
          onMouseEnter: () => show(0),
          onMouseLeave: () => hide(HOVER_DETAIL_LEAVE_GRACE_MS),
          sx: theme => ({ ...surfaceSx(theme), maxWidth }),
        },
      }}
      title={content}
    >
      {children({
        onBlur: () => {
          releasePress();
          hide(0);
        },
        onFocus: () => {
          if (pressing.current) return;
          show(0);
        },
        onMouseDown: () => {
          pressing.current = true;
          window.addEventListener('mouseup', releasePress, { once: true });
        },
        onMouseEnter: () => show(HOVER_DETAIL_OPEN_DELAY_MS),
        onMouseLeave: () => {
          releasePress();
          hide(HOVER_DETAIL_LEAVE_GRACE_MS);
        },
        tabIndex: 0,
      })}
    </Tooltip>
  );
}
